/** The distinct condition shapes the directory row can draw. */
export type WeatherGlyphKind =
  | "clear"
  | "partly"
  | "cloud"
  | "fog"
  | "rain"
  | "sleet"
  | "snow"
  | "thunder";

/**
 * The MET Norway `symbol_code` bases that are not precipitation. Precipitation is
 * read off the code itself below, so the vocabulary needs no row per code —
 * including MET's own `lightssleet…`/`lightssnow…` spellings.
 */
const KIND_BY_BASE: Record<string, WeatherGlyphKind> = {
  clearsky: "clear",
  fair: "clear",
  partlycloudy: "partly",
  cloudy: "cloud",
  fog: "fog",
};

/** MET's day/night suffix; the far north's `polartwilight` is drawn as day. */
const VARIANT = /_(day|night|polartwilight)$/;

/**
 * What the code says is falling. The order decides the shape: anything naming
 * thunder draws a bolt, and sleet is checked before rain so `rainandsleet` and
 * `sleetandthunder` land on the sleet shape rather than the rain one.
 */
function precipitatingKind(base: string): WeatherGlyphKind | null {
  if (base.includes("thunder")) return "thunder";
  if (base.includes("snow")) return "snow";
  if (base.includes("sleet")) return "sleet";
  if (base.includes("rain")) return "rain";
  return null;
}

/** The shape and the day/night variant for one condition code. */
export interface WeatherGlyphShape {
  kind: WeatherGlyphKind;
  night: boolean;
}

/**
 * The shape and the day/night variant for one MET `symbol_code`, or null for a
 * code we do not know — a missing icon beats a wrong one.
 *
 * The parameter is `unknown` on purpose: this is the boundary where provider data
 * becomes a decision, and a payload that carried no usable code must leave the row
 * without an icon rather than throw mid-render and take the directory with it.
 */
export function weatherGlyphKind(symbolCode: unknown): WeatherGlyphShape | null {
  if (typeof symbolCode !== "string" || !symbolCode) return null;
  const match = VARIANT.exec(symbolCode);
  const base = match ? symbolCode.slice(0, -match[0].length) : symbolCode;
  const kind = KIND_BY_BASE[base] ?? precipitatingKind(base);
  return kind ? { kind, night: match?.[1] === "night" } : null;
}

/** The current reading both public surfaces draw: whole degrees and its condition shape. */
export interface CurrentWeatherReading {
  degrees: number;
  /** The reading as both surfaces print it, e.g. `27°C`. */
  label: string;
  glyph: WeatherGlyphShape | null;
}

/**
 * The two payload fields that carry the current reading, as both surfaces state
 * them: `LandingCityWeather` (the directory) has both required, while the guide's
 * `CityLiveWeather` leaves them optional, because a page baked before the header
 * drew the condition has neither.
 */
export interface CurrentWeatherSource {
  temperatureC?: number;
  symbolCode?: string;
}

/**
 * The current weather as the directory row and the guide header both show it:
 * the same whole-degree rounding, the same `°C` unit and the same condition
 * glyph, read off the same `city_weather` row, so one city cannot read `27°C` in
 * the directory and something else in its own guide. Null when the payload
 * carries no usable temperature — a missing reading beats a rounded guess.
 */
export function readCurrentWeather(
  weather: CurrentWeatherSource | null | undefined,
): CurrentWeatherReading | null {
  const temperatureC = weather?.temperatureC;
  if (typeof temperatureC !== "number" || !Number.isFinite(temperatureC)) return null;
  const degrees = Math.round(temperatureC);
  return { degrees, label: `${degrees}°C`, glyph: weatherGlyphKind(weather?.symbolCode) };
}

/** The forecast's next-24 h range as both surfaces print it, e.g. `18–27°C`. */
export function forecastRangeLabel(minC: number, maxC: number): string {
  return `${Math.round(minC)}–${Math.round(maxC)}°C`;
}
